import React, { useRef, useState } from 'react';
import { IconScout } from '@/components/ui/images/IconScout';
import { Tooltip, type TooltipPlacement } from '@/components/ui/tooltips/Tooltip';
import { colors } from '@/assets/styles/colors';


export interface InfoBubbleProps {
  /** Tooltip body text shown on hover */
  text: string;
  /** Optional tooltip title shown above the body text */
  title?: string;
  /**
   * Side the tooltip opens towards. Defaults to `'top'`.
   * Because positioning is CSS-only (`strategy="absolute"`, see below), the tooltip does NOT
   * flip on its own when it runs out of room — pass `'bottom'` when the bubble sits near the
   * top of the viewport (e.g. KPI cards in a page header), or it renders off-screen.
   */
  placement?: TooltipPlacement;
  strategy?: 'auto' | 'fixed' | 'absolute';
  className?: string;
  'data-testid'?: string;
}

/**
 * Inline info icon that shows a tooltip on hover.
 * Siempre renderiza el ícono "info" — es el único definido en Figma para este componente; no
 * exponer una variante configurable (question/exclamation fueron errores de una iteración previa).
 * Hovering reveals a Tooltip with `text`. By default uses `strategy="absolute"`, so the tooltip
 * is positioned relative to the icon, avoiding all viewport/transform issues. Pass
 * `strategy="auto"` when an ancestor has `overflow` and would clip the bubble (see the `strategy`
 * prop).
 *
 * Layout strategy:
 * - Layer 1 (outer span): inline-block, height:0, width:20px. Reserves horizontal
 *   space but contributes zero vertical height → no line box inflation.
 * - Layer 2 (inner span): position:absolute, 20×20px, centered via top:-10px.
 *   Contains the icon, white background mask, and event handlers.
 * - Tooltip: with the default strategy it is position:absolute relative to Layer 2;
 *   with `'auto'` it is position:fixed and anchored to Layer 2 via `anchorRef`.
 */
export const InfoBubble: React.FC<InfoBubbleProps> = ({
  text,
  title,
  placement = 'top',
  strategy = 'absolute',
  className = '',
  'data-testid': dataTestId,
}) => {
  const anchorRef = useRef<HTMLSpanElement>(null);
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <span
      className={`inline-block ml-1 ${className}`}
      style={{
        width: '20px',
        height: '0',
        overflow: 'visible',
        position: 'relative',
        verticalAlign: 'middle',
        zIndex: 1,
      }}
    >
      <span
        ref={anchorRef}
        style={{
          position: 'absolute',
          top: '-11px',
          left: '0',
          width: '20px',
          height: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
        }}
        tabIndex={0}
        aria-label={text}
        onMouseEnter={() => setShowTooltip(true)}
        onMouseLeave={() => setShowTooltip(false)}
        onFocus={() => setShowTooltip(true)}
        onBlur={() => setShowTooltip(false)}
        data-testid={dataTestId ?? 'info-bubble'}
      >

        <IconScout
          name="info"
          size="lg"
          color={colors.primary.auxiliar}
          data-testid="info-bubble-icon"
        />
        {showTooltip && (
          <Tooltip
            title={title}
            body={text}
            anchorRef={anchorRef}
            strategy={strategy}
            placement={placement}
            maxWidth={300}
          />
        )}
      </span>
    </span>
  );
};

export default InfoBubble;
